/**
 * Money owed to the shop.
 *
 * A small shop in Bethlehem lets people it knows take something and settle at
 * the end of the month. That is a real part of the business, so it is a real
 * screen: who owes what, how long it has been sitting there, and a way to take
 * the money when they come in with it.
 *
 * A payment here is not a sale. Nothing is bought, no VAT arises - the goods
 * left on the day of the sale on account. It is money arriving late, so it
 * reaches the drawer and nothing else.
 */
import { useCallback, useEffect, useState } from 'react';
import { usePlural, useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useAuth } from '../../core/state/auth.js';
import { useToast } from '../../core/state/toast.js';
import { api } from '../../core/api.js';
import { shopKey } from '../../core/shop.js';
import { Button, Empty, Field, Loading, Modal, MoneyInput, Pill } from '../../core/ui/ui.js';

interface Row {
  id: string;
  name: string;
  phone: string | null;
  balance: number;
  first_sale_at: string | null;
  last_paid_at: string | null;
}

/** Days since a date, for "how long has this been outstanding". */
function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

export function Owed() {
  const t = useT();
  const plural = usePlural();
  const { money, date } = useSettings();
  const { can } = useAuth();
  const [data, setData] = useState<{ rows: Row[]; total: number; count: number } | null>(null);
  const [paying, setPaying] = useState<Row | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api<{ rows: Row[]; total: number; count: number }>('/reports/receivables'));
    } catch {
      setData({ rows: [], total: 0, count: 0 });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!data) return <Loading />;
  if (data.rows.length === 0) {
    return <Empty icon="🤝" title={t('tab.nobody')} hint={t('tab.nobodyHint')} />;
  }

  return (
    <div className="col">
      <div className="grid c4">
        <div className="kpi accent">
          <div className="k">{t('tab.total')}</div>
          <div className="v tnum">{money(data.total)}</div>
          <div className="d">
            {plural(data.count, {
              one: 'tab.peopleOne',
              two: 'tab.peopleTwo',
              few: 'tab.people',
              many: 'tab.peopleMany',
            })}
          </div>
        </div>
      </div>

      <div className="card flush">
        <div className="table-wrap">
          <table className="cards">
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th>{t('common.phone')}</th>
                <th>{t('tab.since')}</th>
                <th>{t('tab.lastPaid')}</th>
                <th className="num">{t('tab.owes')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const age = daysSince(r.first_sale_at);
                return (
                  <tr key={r.id}>
                    <td className="full strong" data-label={t('common.name')}>
                      {r.name}
                    </td>
                    <td className="tnum" data-label={t('common.phone')}>
                      {r.phone ?? ''}
                    </td>
                    <td data-label={t('tab.since')}>
                      {age === null ? '—' : <Pill kind={age > 60 ? 'danger' : age > 30 ? 'warn' : undefined}>{t('common.days', { n: age })}</Pill>}
                    </td>
                    <td data-label={t('tab.lastPaid')}>
                      {r.last_paid_at ? date(r.last_paid_at) : <span className="muted">{t('tab.never')}</span>}
                    </td>
                    <td className="num tnum strong" data-label={t('tab.owes')}>
                      {money(r.balance)}
                    </td>
                    <td className="num">
                      {can('customer.tab') ? (
                        <Button size="sm" variant="primary" onClick={() => setPaying(r)}>
                          {t('tab.takePayment')}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {paying ? (
        <TakePayment
          customer={paying}
          onClose={() => setPaying(null)}
          onPaid={() => {
            setPaying(null);
            void load();
          }}
        />
      ) : null}
    </div>
  );
}

/** Somebody has come in with money against what they owe. */
export function TakePayment({
  customer,
  onClose,
  onPaid,
}: {
  customer: { id: string; name: string; balance: number };
  onClose: () => void;
  onPaid: () => void;
}) {
  const t = useT();
  const { money } = useSettings();
  const toast = useToast();
  const [amount, setAmount] = useState(customer.balance);
  const [method, setMethod] = useState<'cash' | 'card'>('cash');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      // If the shop counts its drawer, this belongs to the shift it landed in.
      const shift = localStorage.getItem(shopKey('pos.shift'));
      await api(`/exchange/customers/${customer.id}/payment`, {
        body: {
          amount,
          method,
          note: note.trim() || null,
          shiftId: shift ? (JSON.parse(shift) as { id: string }).id : null,
        },
      });
      toast.ok(t('tab.paid'));
      onPaid();
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t('tab.takePaymentFrom', { name: customer.name })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <Button
            variant="primary"
            size="lg"
            busy={busy}
            disabled={amount <= 0 || amount > customer.balance}
            onClick={() => void save()}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="pay-due">
        <div className="k">{t('tab.owesNow')}</div>
        <div className="v tnum">{money(customer.balance)}</div>
      </div>

      <Field label={t('tab.howMuch')} error={amount > customer.balance ? t('tab.tooMuch') : undefined}>
        <MoneyInput className="big" value={amount} onValue={setAmount} autoFocus />
      </Field>
      <div className="btn-row">
        <Button size="sm" onClick={() => setAmount(customer.balance)}>
          {t('tab.payAll')}
        </Button>
      </div>

      <Field label={t('pay.method')}>
        <div className="btn-row">
          <Button variant={method === 'cash' ? 'primary' : 'default'} onClick={() => setMethod('cash')}>
            {t('pay.cash')}
          </Button>
          <Button variant={method === 'card' ? 'primary' : 'default'} onClick={() => setMethod('card')}>
            {t('pay.card')}
          </Button>
        </div>
      </Field>

      <Field label={t('common.note')}>
        <input value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}
